/**
 * src/components/ClerkBridge.tsx
 *
 * Bridges Clerk's session state to our AuthContext. Mounted once at the
 * app root; fires nothing until Clerk reports a signed-in user without
 * matching Harper tokens in AuthContext, at which point it exchanges the
 * Clerk session token for a Harper pair via /AuthClerk.
 *
 * This bridge is what turns "Clerk finished sign-in" (via their <SignIn />
 * widget or the OAuth callback route) into "our app has a valid Bearer."
 * Every protected call flows through the existing harperClient unchanged
 * once the exchange completes.
 *
 * Failure modes and how they're handled:
 *   - Clerk token verify fails server-side → clear both states so the
 *     user drops back to the sign-in screen with a toast.
 *   - Provider conflict (existing email account tries Google or vice
 *     versa) → surface the server message to the user, sign them out
 *     of Clerk so they don't re-bridge in a loop.
 *   - Network error → leave Clerk signed in, retry on next render when
 *     the user does something else. Don't swallow into "user signed
 *     out" permanently.
 */

import { useEffect, useRef } from 'react';
import { useAuth as useClerkAuth, useSession, useClerk } from '@clerk/clerk-react';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from '../context/SnackbarContext';
import { exchangeClerkToken, AuthError } from '../api/authClient';

export function ClerkBridge() {
  const { isLoaded, isSignedIn } = useClerkAuth();
  const { session } = useSession();
  const clerk = useClerk();
  const { isAuthenticated, isRestoring, signIn } = useAuth();
  const { showSnackbar } = useSnackbar();

  // Track whether an exchange is currently in flight for THIS session id.
  // Without this guard, React's strict-mode double-mount + the Clerk hooks'
  // rapid state transitions can fire two concurrent /AuthClerk posts per
  // sign-in, which succeeds twice but writes two RefreshToken rows.
  const inFlightSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Wait until Clerk finishes loading before making any judgment —
    // `isSignedIn` is unreliable before `isLoaded` flips true.
    if (!isLoaded) return;
    // Also wait for AuthContext's own restore path to finish so we
    // don't race "stored Harper refresh token" vs "Clerk session" —
    // whichever lands first wins.
    if (isRestoring) return;
    if (!isSignedIn || !session) return;
    // AuthContext already has Harper tokens — nothing to do. Either we
    // just finished an exchange or AuthContext restored from localStorage.
    if (isAuthenticated) return;
    // Dedup concurrent exchanges for the same Clerk session id.
    if (inFlightSessionIdRef.current === session.id) return;
    inFlightSessionIdRef.current = session.id;

    (async () => {
      try {
        const clerkToken = await session.getToken();
        if (!clerkToken) throw new AuthError('network_error', 'Could not read Clerk session.');
        const res = await exchangeClerkToken(clerkToken);
        signIn(
          { access_token: res.access_token, refresh_token: res.refresh_token, expires_in: res.expires_in },
          res.email,
        );
      } catch (err) {
        // Provider conflict: surface the exact server message. The user
        // also needs to be signed out of Clerk so they're not stuck in a
        // "Clerk says yes, Harper says no" loop on the next render.
        if (err instanceof AuthError && err.type === 'provider_conflict') {
          showSnackbar(err.message, { variant: 'error' });
          try { await clerk.signOut(); } catch { /* noop */ }
        } else if (err instanceof AuthError && err.type === 'refresh_rejected') {
          // Clerk says signed-in but Harper rejected the session. Probably
          // a token misconfiguration (JWT template drift, expired session
          // mid-flight). Sign them out of Clerk so they can try again.
          showSnackbar('Sign-in expired — please try again.', { variant: 'error' });
          try { await clerk.signOut(); } catch { /* noop */ }
        } else {
          showSnackbar(
            err instanceof Error ? err.message : 'Could not complete sign-in.',
            { variant: 'error' },
          );
        }
      } finally {
        // Clear the in-flight guard so a subsequent sign-out + sign-in
        // can re-exchange. Keep using the session id check for strict-
        // mode double-invocation protection.
        inFlightSessionIdRef.current = null;
      }
    })();
  }, [isLoaded, isRestoring, isSignedIn, isAuthenticated, session, signIn, showSnackbar, clerk]);

  return null;
}
